// src/lib/firstAider.ts — Phase 8: First Aider Callout client helpers.
//
// Palette + shared types + the six API helpers. Mirrors lib/quickMessages.ts.
//
// The emergency red family (#B8362A ...) lives HERE and in the First Aider
// components ONLY — never in the theme tokens (gotcha #2). It is deliberately
// a few shades darker/punchier than the generic alert red (#B83A2B in theme.ts)
// so the two banners read as different even before colour is processed.
//
// ids are small IDENTITY values; the API returns them as numbers and we keep
// them as numbers throughout (well under MAX_SAFE_INTEGER for NYMR's volume).

import { apiFetch } from "../api";

// ---------------------------------------------------------------------------
// Emergency palette (hex literals, by design — see gotcha #2).
// ---------------------------------------------------------------------------

export const FA = {
  red:       "#B8362A", // the signature — exclusive to First Aider Callout
  redDk:     "#8A2A1F",
  redDeep:   "#6A1C12",
  redLt:     "#E89E94",
  bg:        "#FCE8E4",
  tint:      "#FFF4F1",
  // status colours
  go:        "#3F6B4F", // "on the way" pine green (distinct from theme sage)
  goBg:      "#DCE8DD",
  goLt:      "#A8C2AC",
  stop:      "#8A2A1F", // "cannot attend" — same as redDk by intent
  stopBg:    "#F8D9D2",
  pending:   "#9F8B6B", // matches theme inkMute
  pendingBg: "#F0E7D0",
  // the white pulse glow used on red backgrounds
  whiteGlow: "#FFEEEA",
} as const;

export const STATION_CHIPS = ["Pickering", "Grosmont", "Goathland", "Levisham", "On train"] as const;

export const ETA_VALUES = [5, 10, 15, 30, 60] as const;
export type EtaMinutes = (typeof ETA_VALUES)[number];

export const THREAD_POLL_MS = 10_000;

export type AlertStatus = "pending" | "read" | "on_way" | "declined";
export type IncidentStatus = "active" | "resolved" | "auto_resolved";

// ---------------------------------------------------------------------------
// API response shapes
// ---------------------------------------------------------------------------

export interface OnDutyCountResponse {
  /** Total flagged first aiders (is_first_aider = 1), incl. the viewer. */
  registeredCount: number;
  /** Of those, how many are signed in for duty right now. */
  signedInCount: number;
  /** registered minus the caller — the actual fan-out size (who'd be alerted). */
  alertableCount: number;
  callerPhone: string | null;
}

export interface RosterEntry {
  id: number;
  firstName: string;
  lastName: string;
  initials: string;
  signedIn: boolean;
  hasEmail: boolean;
  canText: boolean;
  /** Mobile number for tap-to-call next to the name. Null when none on file. */
  phone: string | null;
}

export interface RosterResponse {
  registeredCount: number;
  signedInCount: number;
  firstAiders: RosterEntry[];
}

export type MyCalloutRole = "caller" | "responder" | "admin";

export interface ActiveCallout {
  incidentId: number;
  location: string;
  casualty: string;
  nature: string;
  isLifeThreatening: boolean;
  called999: boolean;
  createdAt: string;
  caller: { firstName: string | null; lastName: string | null; initials: string; phone: string | null };
  /** My relationship to this incident — drives the call-to-action wording. */
  myRole: MyCalloutRole;
  /** My response, if I'm a recipient; null if I'm the caller / an admin only. */
  myStatus: AlertStatus | null;
  myAlertId: number | null;
  tally: Tally;
}

export interface ActiveResponse {
  callouts: ActiveCallout[];
}

export interface IncidentCaller {
  id: number | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  deleted: boolean;
}

export interface Responder {
  alertId: number;
  user: { id: number; firstName: string | null; lastName: string | null; initials: string };
  status: AlertStatus;
  etaMinutes: number | null;
  respondedAt: string | null;
}

export interface Tally {
  onWay: number;
  cannot: number;
  pending: number;
}

export interface Incident {
  id: number;
  caller: IncidentCaller;
  location: string;
  casualty: string;
  nature: string;
  isLifeThreatening: boolean;
  called999: boolean;
  status: IncidentStatus;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: { id: number; firstName: string | null; lastName: string | null } | null;
}

export interface IncidentResponse {
  incident: Incident;
  responders: Responder[];
  tally: Tally;
}

export interface CreateIncidentBody {
  location: string;
  casualty: string;
  nature: string;
  isLifeThreatening: boolean;
  called999: boolean;
}

export interface CreateIncidentResponse {
  incidentId: number;
  createdAt: string;
  alertsSent: number;
}

// ---------------------------------------------------------------------------
// API helpers
//
// On error, apiFetch throws ApiError(status, body, message). Callers that need
// to branch on the structured error read err.body.error:
//   POST /incidents 400 { error: "no_caller_phone", reason }
//   POST /incidents 409 { error: "no_on_duty_first_aiders", onDutyCount }
//   PATCH .../alerts/:id   409 { error: "incident_closed" }
//   PATCH .../incidents/:id 409 { error: "already_resolved" }
// ---------------------------------------------------------------------------

export function fetchOnDutyCount(): Promise<OnDutyCountResponse> {
  return apiFetch<OnDutyCountResponse>("/api/me/first-aider/on-duty-count");
}

export function fetchRoster(): Promise<RosterResponse> {
  return apiFetch<RosterResponse>("/api/me/first-aider/roster");
}

/** "5 registered · 1 signed in" — the count caption used on the tile + form. */
export function registeredSignedInCaption(registered: number, signedIn: number): string {
  const reg = `${registered} first aider${registered === 1 ? "" : "s"} registered`;
  return `${reg} · ${signedIn} signed in`;
}

export function fetchActiveCallouts(): Promise<ActiveResponse> {
  return apiFetch<ActiveResponse>("/api/me/first-aider/active");
}

export function fetchIncident(id: number): Promise<IncidentResponse> {
  return apiFetch<IncidentResponse>(`/api/first-aider/incidents/${id}`);
}

export function postIncident(body: CreateIncidentBody): Promise<CreateIncidentResponse> {
  return apiFetch<CreateIncidentResponse>("/api/first-aider/incidents", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function patchAlert(
  alertId: number,
  body: { readAt: true } | { status: "declined" } | { status: "on_way"; etaMinutes: EtaMinutes },
): Promise<{ alertId: number; status: AlertStatus; etaMinutes: number | null; readAt: string | null; respondedAt: string | null }> {
  return apiFetch(`/api/first-aider/alerts/${alertId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function resolveIncident(id: number): Promise<{
  incidentId: number;
  status: IncidentStatus;
  resolvedAt: string;
  resolvedBy: { id: number; firstName: string | null; lastName: string | null };
}> {
  return apiFetch(`/api/first-aider/incidents/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "resolved" }),
  });
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** "09:41" — time-of-day in 24h for the thread summary's mono stamp. */
export function clockHM(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** "3 MIN" / "1 HR 5 MIN" — elapsed since posted, for the thread summary. */
export function elapsedShort(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "JUST NOW";
  const mins = Math.max(0, Math.floor((now.getTime() - t) / 60000));
  if (mins < 1) return "JUST NOW";
  if (mins < 60) return `${mins} MIN`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} HR` : `${h} HR ${m} MIN`;
}

/** The closed-banner snippet: "Life-threatening · {nature}" (severity first). */
export function bannerSnippet(a: { isLifeThreatening: boolean; nature: string }): string {
  return a.isLifeThreatening ? `Life-threatening · ${a.nature}` : a.nature;
}

export function callerName(c: { firstName: string | null; lastName: string | null; deleted?: boolean }): string {
  if (c.deleted) return "Former colleague";
  return `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "Unknown";
}

export function etaLabel(v: number): string {
  return v >= 60 ? "60+ min" : `${v} min`;
}
